import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getConsultationPath } from "../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../constants/webLeadModules";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import "./Disclaimer.css";

const DISCLAIMERS = [
  {
    id: "general",
    num: "01",
    icon: "fa-shield-halved",
    title: "General Disclaimer",
    color: "#2563eb",
    colorEnd: "#60a5fa",
    content:
      "Wealth Compounders is a financial advisory and distribution firm. Investments in securities, mutual funds, commodities, and other financial instruments are subject to market risks. Past performance is not indicative of future results.",
  },
  {
    id: "investment-risk",
    num: "02",
    icon: "fa-chart-line",
    title: "Investment Risk Disclaimer",
    color: "#0891b2",
    colorEnd: "#38bdf8",
    content:
      "Market conditions can fluctuate due to economic, political, and global factors. Investors are advised to carefully read all scheme-related documents, offer documents, and disclosures before investing. Investment decisions should be based on individual risk appetite, financial goals, and time horizon.",
  },
  {
    id: "no-assurance",
    num: "03",
    icon: "fa-circle-exclamation",
    title: "No Assurance Disclaimer",
    color: "#f59e0b",
    colorEnd: "#fcd34d",
    content:
      "Wealth Compounders does not guarantee any returns, profits, or capital protection unless explicitly stated in the product documentation. All examples, illustrations, and projections shown are for educational and informational purposes only.",
  },
  {
    id: "advisory",
    num: "04",
    icon: "fa-handshake",
    title: "Advisory & Distribution Disclaimer",
    color: "#10b981",
    colorEnd: "#6ee7b7",
    content:
      "Wealth Compounders may act as a distributor or advisor for certain financial products. Fees, commissions, or incentives may be received from product manufacturers, wherever applicable, as per regulatory guidelines.",
  },
  {
    id: "tax",
    num: "05",
    icon: "fa-file-invoice-dollar",
    title: "Tax Disclaimer",
    color: "#7c3aed",
    colorEnd: "#a78bfa",
    content:
      "Tax laws are subject to change from time to time. Tax benefits mentioned, if any, depend on individual circumstances and prevailing tax regulations. Clients are advised to consult their tax advisor for personalized tax advice.",
  },
  {
    id: "regulatory",
    num: "06",
    icon: "fa-scale-balanced",
    title: "Regulatory Disclaimer",
    color: "#ef4444",
    colorEnd: "#fca5a5",
    content:
      "All services are offered in accordance with applicable laws and regulations prescribed by relevant authorities. Investments are subject to regulatory approvals and compliance norms as applicable.",
  },
  {
    id: "mutual-fund",
    num: "07",
    icon: "fa-chart-pie",
    title: "Mutual Fund Disclaimer",
    color: "#0d9488",
    colorEnd: "#5eead4",
    content:
      "Investments are subject to market risks. Please read the scheme information and other related documents carefully before investing. Past performance is not indicative of future results. Realization of goals depends on market factors and regular investing.",
  },
  {
    id: "calculators",
    num: "08",
    icon: "fa-calculator",
    title: "Calculators Disclaimer",
    color: "#6366f1",
    colorEnd: "#a5b4fc",
    content:
      "The Calculators are designed to assist you in determining the appropriate amount. These Calculators alone are not sufficient and shouldn't be used for development or implementation of an investment strategy. There is no warranty about the currency of the calculators/ reckoners. The examples do not purport to represent the performance of any security or investments. The recipient is advised to consult his or her advisor/tax consultant prior to arriving at any investment decision. Past performance may or may not be sustained in future and is not a guarantee of any future returns.",
  },
];

const BADGES = ["SEBI", "AMFI", "NSE", "BSE"];

export default function Disclaimer() {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  const toggle = (id) => setOpenId(openId === id ? null : id);

  return (
    <div className="wdis-wrapper">
      <Navbar />

      {/* ── HERO ── */}
      <section className="wdis-hero">
        <div className="wdis-hero-bg" />
        <div className="wdis-hero-dotgrid" />
        <div className="wdis-glow wdis-glow-l" />
        <div className="wdis-glow wdis-glow-r" />

        <div className={`wdis-hero-inner${visible ? " wdis-visible" : ""}`}>
          <div className="wdis-eyebrow">
            <i className="fas fa-gavel" /> Legal &amp; Compliance
          </div>
          <h1 className="wdis-hero-title">
            Mandatory Financial
            <br />
            <span className="wdis-gradient-text">Disclaimers</span>
          </h1>
          <p className="wdis-hero-sub">
            Please read all disclosures carefully before making any investment
            decisions. These disclaimers govern your use of Wealth Compounders'
            services and products.
          </p>
          <div className="wdis-hero-meta">
            <span className="wdis-meta-chip">
              <i className="fas fa-calendar-check" /> Effective: January 2025
            </span>
            <span className="wdis-meta-chip">
              <i className="fas fa-rotate" /> Reviewed Annually
            </span>
            <span className="wdis-meta-chip">
              <i className="fas fa-check-circle" /> SEBI Compliant
            </span>
          </div>
        </div>
      </section>

      {/* ── BODY ── */}
      <section className="wdis-body">
        <div className="wdis-body-bg" />
        <div className="wdis-blob wdis-blob-1" />
        <div className="wdis-blob wdis-blob-2" />

        <div className="wdis-container">
          {/* Warning banner */}
          <div className={`wdis-warning${visible ? " wdis-visible" : ""}`}>
            <div className="wdis-warning-icon">
              <i className="fas fa-triangle-exclamation" />
            </div>
            <p>
              <strong>Important:</strong> Investments in financial markets are
              subject to market risks. Please read all scheme-related documents
              carefully before investing. Wealth Compounders is not responsible
              for any losses arising from investment decisions.
            </p>
          </div>

          {/* Section header */}
          <div className="wdis-section-header">
            <div className="wdis-pill">
              <i className="fas fa-list" /> All Disclaimers
            </div>
            <h2 className="wdis-section-title">
              Our <span className="wdis-gradient-text">Disclosures</span>
            </h2>
            <p className="wdis-section-body">
              Click any disclaimer below to expand and read the full disclosure.
            </p>
          </div>

          {/* Accordion cards */}
          <div className="wdis-accordion">
            {DISCLAIMERS.map((d, i) => (
              <div
                key={d.id}
                className={`wdis-card${openId === d.id ? " wdis-card-open" : ""}`}
                style={{
                  "--dc": d.color,
                  "--dc-end": d.colorEnd,
                  animationDelay: `${i * 70}ms`,
                }}
                onClick={() => toggle(d.id)}
              >
                {/* Coloured top bar */}
                <div className="wdis-card-bar" />

                {/* Header — always visible */}
                <div className="wdis-card-header">
                  <div
                    className="wdis-card-icon"
                    style={{
                      background: d.color + "12",
                      border: `1.5px solid ${d.color}25`,
                      color: d.color,
                    }}
                  >
                    <i className={`fas ${d.icon}`} />
                  </div>
                  <div className="wdis-card-title-wrap">
                    <span className="wdis-card-num" style={{ color: d.color }}>
                      {d.num}
                    </span>
                    <h3 className="wdis-card-title">{d.title}</h3>
                  </div>
                  <div className="wdis-card-tags">
                    <span
                      className="wdis-card-tag"
                      style={{
                        color: d.color,
                        background: d.color + "0f",
                        border: `1px solid ${d.color}22`,
                      }}
                    >
                      {i < 3 ? "High Priority" : "Standard"}
                    </span>
                  </div>
                  <div
                    className="wdis-card-chevron"
                    style={{ color: openId === d.id ? d.color : "#94a3b8" }}
                  >
                    <i className="fas fa-chevron-down" />
                  </div>
                </div>

                {/* Body — slides open */}
                <div className="wdis-card-body">
                  <div className="wdis-card-body-overflow">
                    <div
                      className="wdis-card-body-inner"
                      style={{ borderLeftColor: d.color + "40" }}
                    >
                      <p>{d.content}</p>
                    </div>
                  </div>
                </div>

                {/* Hover glow */}
                <div
                  className="wdis-card-glow"
                  style={{
                    background: `radial-gradient(ellipse 70% 50% at 50% 100%, ${d.color}10, transparent 70%)`,
                  }}
                />
              </div>
            ))}
          </div>

          {/* Compliance strip */}
          <div className={`wdis-compliance${visible ? " wdis-visible" : ""}`}>
            {/* Icon */}
            <div className="wdis-compliance-icon">
              <i className="fas fa-shield-halved" />
            </div>

            {/* Text + CTA */}
            <div className="wdis-compliance-body">
              <h3>Regulatory Compliance</h3>
              <p>
                Wealth Compounders operates under the purview of applicable
                financial regulatory authorities. All advisory and distribution
                activities are conducted in accordance with prescribed
                regulatory frameworks. By using our services, you acknowledge
                that you have read, understood, and agreed to all disclaimers.
              </p>
              <button
                className="wdis-compliance-btn"
                onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.DISCLAIMER))}
              >
                Speak to an Advisor <i className="fas fa-arrow-right" />
              </button>
            </div>

            {/* Badges */}
            <div className="wdis-compliance-badges">
              {BADGES.map((b) => (
                <span key={b} className="wdis-badge">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
